import { ConfigProvider, Flex, Form, Input, Select, Space, Switch } from "antd"
import { PropertyFormValues, createPropertyFormValues, getLocation, propertyFormValidationRules } from "./propertyModel"
import { getAll as getAllCountries } from "../../services/countriesService"
import { useContext, useEffect, useState } from "react"
import { getForCountry } from "../../services/statesService"
import VSpace from "../common/VSpace"
import GooglePlacesAutoCompleteInput from "../common/GooglePlacesAutoCompleteInput"
import { PlaceData } from "../common/googlePlacesAutoCompleteModel"
import EditPropertyContext from "./EditPropertyContext"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faNote, faTags } from "@fortawesome/pro-solid-svg-icons"
import MapComponent from "../maps/Map"
import { faMapLocation } from "@fortawesome/pro-duotone-svg-icons"
import Geocoder from "../maps/Geocoder"

const defaultInputStyle = { width: '410px' }
const halfInputStyle = { width: '190px' }

const PropertyInfoForm = () => {
  const { googleMapsApiKey, property, editState, dispatch, showDeletedView } = useContext(EditPropertyContext)
  const initialValues = createPropertyFormValues(property)
  const [stateOptions, setStateOptions] = useState(buildStateOptions(initialValues.countryCode))
  const [stateLabel, setStateLabel] = useState(getStateLabel(initialValues.countryCode))
  const [geoLocatedPosition, setGeolocatedPosition] = useState<{latitude: number, longitude: number} | undefined>()

  const [form] = Form.useForm<PropertyFormValues>()

  const position = getLocation(editState) ?? geoLocatedPosition
  const useGeocoder = position === undefined && property?.address1 !== undefined && property?.id !== 0
  const geocoderAddress = useGeocoder ? `${property.address1}, ${property.city} ${property.state}, ${property.countryCode}` : ''

  useEffect(() => {
    if (property) {
      form.setFieldsValue(createPropertyFormValues(property))
    }
  }, [property])

  const handlePlaceData = (data: PlaceData) => {
    const stateOptions = buildStateOptions(data.countryCode)
    setStateOptions(stateOptions)
    setStateLabel(getStateLabel(data.countryCode))
    if (data.location) {
      dispatch({ type: 'locationChanged', location: data.location })
    }

    form.setFieldsValue({
      address1: data.address1,
      city: data.city,
      zip: data.zip,
      county: data.county,
      countryCode: data.countryCode,
      state: stateOptions.length > 0 ? data.stateShort : data.stateLong
    })

    dispatch({ type: 'formChanged', values: form.getFieldsValue() })
  }

  return <ConfigProvider theme={{
    components: {
      Form: {
        itemMarginBottom: 0
      }
    }
  }}>
    {useGeocoder && <Geocoder googleMapsApiKey={googleMapsApiKey} address={geocoderAddress} onGeocode={setGeolocatedPosition} />}
    <Form layout="vertical" initialValues={initialValues} form={form} disabled={showDeletedView}
      onValuesChange={(x, all) => {
        if (x.countryCode) {
          setStateOptions(buildStateOptions(x.countryCode))
          setStateLabel(getStateLabel(x.countryCode))

          if (all.state) {
            form.setFieldValue('state', undefined)
            form.validateFields(['state'])
          }
        }
      }}
      onFieldsChange={() => dispatch({ type: 'formChanged', values: form.getFieldsValue() })}
    >
      <section>
        <VSpace>
          <Flex align="center" gap={10}>
            <Form.Item name="isActive">
              <Switch />
            </Form.Item>
            <span style={{cursor: showDeletedView ? 'not-allowed' : 'pointer'}}
              onClick={() => {
                if (showDeletedView) {
                  return
                }
                form.setFieldValue('isActive', !form.getFieldValue('isActive'))
              }}
            >
              This property is currently active
            </span>
          </Flex>

          <Flex justify="flex-start" wrap="wrap" gap={50}>
            <Space direction="vertical" size="small">
              <Form.Item label="Property Nickname" name="nickname" style={defaultInputStyle} rules={formRules.nickname}>
                <Input />
              </Form.Item>

              <Form.Item label="Address" name="address1" style={defaultInputStyle} rules={formRules.address1}>
                <GooglePlacesAutoCompleteInput onPlaceData={handlePlaceData} apiKey={googleMapsApiKey} />
              </Form.Item>
              <Form.Item name="address2" style={defaultInputStyle} rules={formRules.address2}>
                <Input />
              </Form.Item>

              <Form.Item label="City" name="city" style={defaultInputStyle} rules={formRules.city}>
                <Input />
              </Form.Item>

              <Space size="large" align="start">
                <Form.Item label="Zip" name="zip" style={halfInputStyle} rules={formRules.zip}>
                  <Input />
                </Form.Item>
                <Form.Item label="County" name="county" style={halfInputStyle} rules={formRules.county}>
                  <Input />
                </Form.Item>
              </Space>

              <Space size="large" align="start">
                <Form.Item label="Country" name="countryCode" style={halfInputStyle} rules={formRules.countryCode}>
                  <Select options={countryOptions} />
                </Form.Item>
                <Form.Item label={stateLabel} name="state" style={halfInputStyle} rules={formRules.state}>
                  { stateOptions.length > 0 ? <Select options={stateOptions} /> : <Input /> }
                </Form.Item>
              </Space>
            </Space>

            <div>
              {!position && <div style={{width: 800, height: 400, border: '1px solid #ccc', borderRadius: 5, display: 'flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center'}}>
                <VSpace>
                  <FontAwesomeIcon icon={faMapLocation} size="7x" />
                  <span>Use Google's autocomplete on the Address field to display a map.</span>
                </VSpace>
              </div>}
              {position && <MapComponent
                apiKey={googleMapsApiKey}
                center={position}
                zoom={16}
                width={800}
                height={400}
                markers={[{position}]}
                onMarkerMoved={showDeletedView ? undefined : marker => dispatch( {type: 'locationChanged', location: marker.position})}
              />}
            </div>
          </Flex>
        </VSpace>
      </section>

      <section>
        <VSpace>
          <Form.Item label={<Space size="small"><FontAwesomeIcon icon={faTags} /><span>Tags</span></Space>} name="tags">
            <Select mode="tags" dropdownStyle={{display: 'none'}} allowClear />
          </Form.Item>

          <Form.Item label={<Space size="small"><FontAwesomeIcon icon={faNote} /><span>Notes</span></Space>} name="notes">
            <Input.TextArea style={{height: '150px'}} />
          </Form.Item>
        </VSpace>
      </section>
    </Form>
  </ConfigProvider>
}

export default PropertyInfoForm

const displayNames: any = {
  nickname: 'Property Nickname',
  address1: 'Address',
  address2: 'Address',
  city: 'City',
  zip: 'Zip',
  countryCode: 'Country',
  state: 'State',
  phoneType: 'Phone type',
  phoneNumber: 'Phone',
  email: 'Email'
}

const formRules = Object.entries(propertyFormValidationRules).reduce((acc:any, [key, value]) => {
  const rules = []
  if (value.required) {
    rules.push({ required: true, message: `${displayNames[key]} is required` })
  }
  if (value.maxLength) {
    rules.push({ type: 'string', max: value.maxLength, message: `${displayNames[key]} may not be longer than ${value.maxLength} characters`})
  }
  acc[key] = rules
  return acc
}, {})

const countryOptions = getAllCountries().map((country) => ({
  label: country.name,
  value: country.code
}))

const buildStateOptions = (countryCode: string) => {
  const states = getForCountry(countryCode) ?? []
  return states.map((state) => ({
    label: state.name,
    value: state.code
  }))
}

const getStateLabel = (countryCode: string) => {
  return getAllCountries().find(c => c.code === countryCode)?.stateLabel ?? 'State / Province / Region'
}
